/**
 * Canonical appointment lifecycle. Payment / billing never belong here.
 *
 * appointmentStatus answers: what has happened with the appointment?
 * billingStatus answers: has / how has it been paid?
 *
 * Paying in advance does not finish a visit. A future slot with
 * `status = completed` or `visit_status = payment_completed` stays Kommende.
 *
 * A slot whose end has passed is not Kommende. With no clinical visit
 * (`checked_in` / `under_treatment` / `treatment_finished`) it is Gennemført.
 * That matches the hourly cron, which later writes `status = completed`
 * once `ends_at` is more than an hour ago, and it covers rows the cron
 * has already promoted. Clinical visit status still wins after the end.
 */
import { CLINIC_TIMEZONE, zonedYmd } from '@/lib/reporting/timezone';

export type AppointmentRecordStatus = 'upcoming' | 'completed' | 'cancelled' | 'no_show';

export type AppointmentVisitStatus =
  | 'checked_in'
  | 'under_treatment'
  | 'treatment_finished'
  | 'payment_completed'
  | null;

export type AppointmentLifecycleStatus =
  | 'upcoming'
  | 'checked_in'
  | 'under_treatment'
  | 'treatment_finished'
  | 'completed'
  | 'no_show'
  | 'cancelled';

/** Patient Aftaler filter chips. Null is only in Alle aftaler. */
export type AppointmentListFilter = 'upcoming' | 'completed' | 'cancelled' | 'no_show';

export type PatientFacingAppointmentStatus =
  | 'upcoming'
  | 'checked_in'
  | 'in_treatment'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export interface ResolveAppointmentStatusInput {
  status: AppointmentRecordStatus | string | null | undefined;
  visitStatus?: string | null;
  startsAt?: Date | string;
  endsAt?: Date | string;
  now?: Date;
}

function asRecordStatus(value: string | null | undefined): AppointmentRecordStatus {
  switch (value) {
    case 'upcoming':
    case 'completed':
    case 'cancelled':
    case 'no_show':
      return value;
    default:
      return 'upcoming';
  }
}

function asVisitStatus(value: string | null | undefined): AppointmentVisitStatus {
  switch (value) {
    case 'checked_in':
    case 'under_treatment':
    case 'treatment_finished':
    case 'payment_completed':
      return value;
    default:
      return null;
  }
}

function appointmentEndMs(
  input: Pick<ResolveAppointmentStatusInput, 'startsAt' | 'endsAt'>,
): number | null {
  const raw = input.endsAt ?? input.startsAt;
  if (raw == null) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * True while the booked slot has not ended.
 *
 * Comparison is on the absolute instant. Appointments are stored as
 * timestamptz, so this is the Europe/Copenhagen wall clock without
 * reading the runtime timezone. Unknown times are not treated as past.
 */
export function appointmentSlotIsUpcoming(
  input: Pick<ResolveAppointmentStatusInput, 'startsAt' | 'endsAt' | 'now'>,
): boolean {
  const endMs = appointmentEndMs(input);
  if (endMs == null) return false;
  const now = input.now ?? new Date();
  return endMs >= now.getTime();
}

function slotHasEnded(
  input: Pick<ResolveAppointmentStatusInput, 'startsAt' | 'endsAt' | 'now'>,
): boolean {
  const endMs = appointmentEndMs(input);
  if (endMs == null) return false;
  const now = input.now ?? new Date();
  return endMs < now.getTime();
}

/**
 * One resolver for calendar, appointment details, Dagens program,
 * Patient Portal, and Patients → Aftaler.
 *
 * Færdig (`treatment_finished`) requires an explicit clinical completion.
 * Payment never produces that: `visit_status = payment_completed`,
 * `appointments.status = completed` from an old settlement, and invoice /
 * Clinio Pay / card / MobilePay / klippekort paid all stay Kommende
 * until the slot end.
 *
 * After the end, the same rows are Gennemført (`completed`), not Kommende.
 * `checked_in` and `under_treatment` stay those badges after the end.
 */
export function resolveAppointmentStatus(
  input: ResolveAppointmentStatusInput,
): AppointmentLifecycleStatus {
  const status = asRecordStatus(input.status);
  const visit = asVisitStatus(input.visitStatus);

  switch (status) {
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    case 'upcoming':
    case 'completed':
      break;
    default: {
      const _exhaustive: never = status;
      throw new Error(`Unhandled appointment record status: ${_exhaustive}`);
    }
  }

  switch (visit) {
    case 'checked_in':
      return 'checked_in';
    case 'under_treatment':
      return 'under_treatment';
    case 'treatment_finished':
      return 'treatment_finished';
    case 'payment_completed':
    case null:
      return slotHasEnded(input) ? 'completed' : 'upcoming';
    default: {
      const _exhaustive: never = visit;
      throw new Error(`Unhandled visit status: ${_exhaustive}`);
    }
  }
}

/**
 * Chip, badge, and Kommende/Tidligere share this.
 *
 * Kommende is only a slot that has not ended and is not cancelled,
 * no-show, or clinically finished. A past open visit (Tjekket ind /
 * Under behandling) is in neither Kommende nor Gennemført; it stays
 * in Alle aftaler under Tidligere with its visit badge.
 */
export function appointmentListFilter(
  input: ResolveAppointmentStatusInput,
): AppointmentListFilter | null {
  const lifecycle = resolveAppointmentStatus(input);
  switch (lifecycle) {
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    case 'completed':
    case 'treatment_finished':
      return 'completed';
    case 'upcoming':
      return 'upcoming';
    case 'checked_in':
    case 'under_treatment':
      return slotHasEnded(input) ? null : 'upcoming';
    default: {
      const _exhaustive: never = lifecycle;
      throw new Error(`Unhandled appointment lifecycle: ${_exhaustive}`);
    }
  }
}

/** Kommende / I dag group. Same set as the Kommende filter chip. */
export function appointmentIsListedUpcoming(input: ResolveAppointmentStatusInput): boolean {
  return appointmentListFilter(input) === 'upcoming';
}

/** Civil "I dag" in the clinic timezone, not the runtime timezone. */
export function appointmentIsOnClinicDay(
  startsAt: Date | string,
  now: Date = new Date(),
  timeZone: string = CLINIC_TIMEZONE,
): boolean {
  const startMs = new Date(startsAt).getTime();
  if (!Number.isFinite(startMs)) return false;
  return zonedYmd(new Date(startMs), timeZone) === zonedYmd(now, timeZone);
}

export function toPatientFacingAppointmentStatus(
  lifecycle: AppointmentLifecycleStatus,
): PatientFacingAppointmentStatus {
  switch (lifecycle) {
    case 'upcoming':
      return 'upcoming';
    case 'checked_in':
      return 'checked_in';
    case 'under_treatment':
      return 'in_treatment';
    case 'treatment_finished':
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    default: {
      const _exhaustive: never = lifecycle;
      throw new Error(`Unhandled appointment lifecycle: ${_exhaustive}`);
    }
  }
}

export function appointmentStatusLabelKey(status: AppointmentLifecycleStatus): string {
  return `appointments.legend.${status}`;
}

export function canAdvanceVisitWorkflow(status: AppointmentLifecycleStatus): boolean {
  switch (status) {
    case 'upcoming':
    case 'checked_in':
    case 'under_treatment':
    case 'completed':
      return true;
    case 'treatment_finished':
    case 'cancelled':
    case 'no_show':
      return false;
    default: {
      const _exhaustive: never = status;
      throw new Error(`Unhandled appointment lifecycle: ${_exhaustive}`);
    }
  }
}
